"use client";
import { useContext, createContext, useMemo } from "react";
import {
  toJS,
  makeObservable,
  observable,
  action,
  computed,
  transaction
} from "mobx";
import { observer } from "mobx-react-lite";

// internal APIs
import logging from "./logging";
import gql from "./gql";
import analytics from "./analytics";
import utilities from "./utilities";
import device from "./device";
//
import user from "./user";

// entities
import model from "./model";
import stars from "./stars";
import tasks from "./tasks";
import videos from "./videos";
// views
import reader from "./reader";
import search from "./search";
import deploy from "./deploy";
// high-level components
import layout from "./layout";
import snackbar from "./snackbar";
import bottomSheet from "./bottomSheet";
import dialog from "./dialog";
import menu from "./menu";
// components
import agent from "./agent";
//
class GlobalStore {
  constructor() {
    const stores = {
      // APIs
      logging,
      gql,
      device,
      user,
      utilities,
      analytics,
      tasks,
      stars,
      model,
      videos,
      layout,
      // views
      reader,
      search,
      deploy,
      // high level components
      dialog,
      snackbar,
      menu,
      bottomSheet,
      agent
    };

    for (const storeName in stores) {
      var Store = stores[storeName];

      this[storeName] = new Store(makeMobxStore, this);
    }

    function makeMobxStore(store) {
      const reset = () => {
        transaction(() => {
          const setters = Object.getOwnPropertyNames(store.set);
          let i = setters.length;

          while (i--) {
            store.set[setters[i]]();
          }
        });
      };

      reset();

      const storePrototype = Object.getOwnPropertyDescriptors(
        Object.getPrototypeOf(store)
      );
      const _computed = {};

      for (const prop in storePrototype) {
        if (storePrototype[prop].get !== undefined) {
          _computed[prop] = computed;
        }
      }

      const observables = {};
      const actions = Object.keys(store.set).reduce((actions, prop) => {
        actions[prop] = action;
        observables[prop] = observable;

        return actions;
      }, {});

      makeObservable(store.set, actions);
      makeObservable(store, { ...observables, ..._computed });
      // add to JS as part of all stores

      store.toJS = toJS;
      // return generic reset
      return reset;
    }
  }
}

const Context = createContext();

export { observer };
export const useStore = () => useContext(Context);

// We keep this out of the component to divorce it from the rendering tree of the root layout
// I have not been able to prove it by recreating the bug, but stores are recreated if the root layout rerenders due to
// hydration mismatch, which could be due to chunk download failure, or mismatches in the DOM because of locale based differences
// we're going to try this out and see if our many events problem disappears. If it does, then there was something happening at the root layout level
// that caused StoreProvider to unmount and remount

// NOTE the thing that's likely causing the problem is that onAuthStateChanged is the only thing that fires the analytics call that we saw getting fired many many times.
// The other thing to take into consideration is that everytime we've seen a max call stack size exceeded error, it's been proceeded by a chunk failure error
// so this may be the next client recursively calling itself to update state from the server over and over again.
// It also may be that during high load chunks become hard to get, and the problem is exacerbated leading to more and more chunk failures and ultimately
// a client that hits the call stack limit.
const GLOBAL_STORE = new GlobalStore();

export function StoreProvider({ children }) {
  return <Context.Provider value={GLOBAL_STORE}>{children}</Context.Provider>;
}
